import { Signal } from '@preact/signals-react';
import produce from 'immer';
import { toast } from 'react-toastify';
import { css } from 'styled-components';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import {
  HistoryCheckpoint,
  SessionListEntry,
  useAiMetadataApi,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { CompoundChange } from '@/pkg/sdk';
import {
  AiChatMessage,
  SelectedAgent,
} from '@/features/ai-agents/shared/models';
import { extractCodeBlocks } from '@/features/editor/widgets/code-editors/use-code-editor';
import { getQueryParam } from '@/utils/browser';
import { extractEditorConfig } from '@/features/ai-agents/chat/editor-config';
import { maybe } from '@/features/details/utils';
import { CodeBlockMutationOptions } from '@/features/editor/widgets/shared/ai-editing-layout/types';

export interface SessionManagerActions {
  onNewSessionId: (session: SessionListEntry) => void;
  onNewMessageSent: (message: AiChatMessage) => void;
  onPromptReply: (
    agentMessage: AiChatMessage,
    userMessage: AiChatMessage,
    sessionId: string,
  ) => void;
  onSessionLoaded: (messages: AiChatMessage[]) => void;
  restoreCheckpoint: (checkpoint: HistoryCheckpoint) => Promise<void>;
}

export interface SessionManagerOptions {
  onCodeBlockMutation?: (options: CodeBlockMutationOptions) => void;
}

export function useSessionManager(
  newChange: Signal<EditorDeclarativeBlock>,
  selectedAgent: Signal<SelectedAgent>,
  lastSavedMessageId: Signal<string>,
  rerender: () => void,
  options?: SessionManagerOptions,
): SessionManagerActions {
  const { restoreSessionCheckpoint } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const onNewSessionId = (session: SessionListEntry) => {
    newChange.value = produce(newChange.value, (draft) => {
      (draft.block.value as CompoundChange).aiSessionId = session.session_id;
    });
  };

  const onSessionLoaded = (messages: AiChatMessage[]) => {
    if (messages?.length > 0) {
      const lastMessage = messages?.[messages.length - 1];
      lastSavedMessageId.value = lastMessage.id;
    }
  };

  const onPromptReply = (
    agentMessage: AiChatMessage,
    _: AiChatMessage,
    sessionId: string,
  ) => {
    newChange.value = produce(newChange.value, (draft) => {
      const block = draft.block.value as CompoundChange;
      block.aiSessionId = sessionId;
      block.lastRunId = agentMessage?.run_id;

      // Extract editor config
      const editorConfig = extractEditorConfig(agentMessage.content);
      if (editorConfig) {
        block.editorConfig = editorConfig;
      }
    });

    const {
      html0: html,
      css0: css,
      javascript0: js,
    } = extractCodeBlocks(agentMessage.content);

    // Default code block mutation behavior
    newChange.value = produce(newChange.value, (draft) => {
      const block = draft.block.value as CompoundChange;
      let aiSectionId = ``;

      if (block.editorConfig?.name) {
        draft.hint = block.editorConfig.name;
      }

      if (options?.onCodeBlockMutation) {
        options.onCodeBlockMutation({
          html,
          css,
          js,
          draft,
          block,
        });
      } else {
        if (html) {
          const div = document.createElement(`div`);
          div.innerHTML = html;
          const aiSection = div.querySelector(`div[id*="loomi_addon"]`);
          aiSectionId = aiSection?.id;
          if (aiSectionId) {
            block.html = html.replaceAll(aiSectionId, draft.editorId);
          } else {
            block.html = html;
          }
        }
        if (css) {
          if (aiSectionId) {
            block.css = css.replaceAll(`#${aiSectionId}`, `#${draft.editorId}`);
          } else {
            block.css = css;
          }
        }
        if (js) {
          if (aiSectionId) {
            block.js = js.replaceAll(aiSectionId, draft.editorId);
          } else {
            block.js = js;
          }
        }
      }
    });

    rerender();
  };

  const onNewMessageSent = (message: AiChatMessage) => {
    const block = newChange.value?.block?.value as CompoundChange;
    const html = block.html || ``;
    const css = block.css || ``;
    const js = block.js || ``;
    const cfg = JSON.stringify(block.editorConfig);

    const isPlaceholder =
      html?.includes(`__loomi_element_placeholder`) ||
      html?.includes(`__visually_element_placeholder`);

    if (!isPlaceholder) {
      if (!message.session_data) {
        message.session_data = {};
      }
      message.session_data[`html_code`] = html;
      message.session_data[`css_code`] = css;
      message.session_data[`js_code`] = js;
      message.session_data[`editor_config`] = cfg;
    }
  };

  const restoreCheckpoint = (checkpoint: HistoryCheckpoint) => {
    let oldHtml;
    let oldCss;
    let oldJs;
    let oldEditorConfig;

    newChange.value = produce(newChange.value, (draft) => {
      const value = draft.block.value as CompoundChange;
      oldHtml = value.html;
      oldCss = value.css;
      oldJs = value.js;
      oldEditorConfig = value.editorConfig;

      value.html = checkpoint.html;
      value.css = checkpoint.css;
      value.js = checkpoint.js;
      value.editorConfig = maybe(
        () => JSON.parse(checkpoint.editor_config),
        ``,
      );
    });

    return restoreSessionCheckpoint(checkpoint.new_session_id, {
      id: selectedAgent.value.id,
      type: selectedAgent.value.type,
    })
      .then(() => {
        newChange.value = produce(newChange.value, (draft) => {
          const value = draft.block.value as CompoundChange;
          value.aiSessionId = checkpoint.new_session_id;
          value.aiFirstSessionId = checkpoint.first_session_id;
          value.lastRunId = checkpoint.run_id;
        });
      })
      .catch((ex) => {
        console.error(`vsly-restore-checkpoint`, ex);

        toast(`Something went wrong when trying to restore a checkpoint`, {
          theme: `colored`,
          type: `error`,
          className: css({
            fontFamily: `JetBrains Mono, Serif`,
            fontWight: `700`,
          }),
        });
        newChange.value = produce(newChange.value, (draft) => {
          const value = draft.block.value as CompoundChange;
          value.html = oldHtml;
          value.css = oldCss;
          value.js = oldJs;
          value.editorConfig = oldEditorConfig;
        });
      });
  };

  return {
    onNewSessionId,
    onNewMessageSent,
    onPromptReply,
    onSessionLoaded,
    restoreCheckpoint,
  };
}
